// ─────────────────────────────────────────────────────────────────────────────
// RexyCalendarPanel — STAGE 2 · STEP 4 (D5): THE CALENDAR — A WINDOW, NOT A SYSTEM.
//
// OFF-THE-SHELF UI: react-calendar (MIT, the known component) restyled to the
// REXY_CONSOLE idiom — the dark base is never overridden (CLAUDE.md UI §1);
// this file only themes and wires, it builds no calendar engine.
//
// READS ONLY: GET /api/rexy/calendar (month presence map) and
// GET /api/rexy/calendar/day (one day's cell). Click a date → the NOTES /
// REPORTS panel for that day beside its timestamped activity — the
// hallucination-audit surface made visual: a report claim with no matching
// activity SHOWS. WHO is agent-level (the chat-WHO ruling) and rendered as
// given — no agent is named or special-cased anywhere in this file.
//
// FAIL-LOUD: every store status the backend names is displayed by name; a day
// with no snapshot renders "no snapshot — explicit absence", never invented
// content. Unreadable ≠ empty, and both are labelled.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import { useCalendar, dayKey } from '../../data/dashboard';
import type { DayCounts } from '../../data/dashboard';

const MONO = REXY_FONT;

// DESKTOP ARRANGEMENT ONLY — this surface renders the grid through
// react-calendar; the portrait phone surface draws its own 7-column grid over
// the SAME feed. The endpoints, the dayKey contract and the month/day GATES
// moved to data/dashboard/useCalendar.ts under the 2026-07-31 "one core, every
// window" ruling. The render below is unchanged.

// The react-calendar restyle — REXY_CONSOLE over the library's light default.
// Scoped to .rexy-cal so nothing else is touched. Dark canvas, cyan accents,
// dimensional hover per the locked aesthetic; no light surface anywhere.
const CAL_CSS = `
.rexy-cal .react-calendar {
  width: 100%;
  background: transparent;
  border: none;
  font-family: ${MONO};
}
.rexy-cal .react-calendar button {
  color: ${REXY_CONSOLE.infText};
  background: transparent;
  font-family: ${MONO};
  font-size: 11px;
  border-radius: 4px;
}
.rexy-cal .react-calendar__navigation button {
  color: ${REXY_CONSOLE.primary};
  font-size: 12px;
  letter-spacing: 1px;
  min-width: 30px;
}
.rexy-cal .react-calendar__navigation button:enabled:hover,
.rexy-cal .react-calendar__navigation button:enabled:focus {
  background: ${REXY_CONSOLE.badgeOkBg};
}
.rexy-cal .react-calendar__month-view__weekdays {
  color: ${REXY_CONSOLE.dimText};
  font-size: 9px;
  letter-spacing: 1px;
}
.rexy-cal .react-calendar__month-view__weekdays abbr { text-decoration: none; }
.rexy-cal .react-calendar__month-view__days__day--neighboringMonth {
  color: ${REXY_CONSOLE.dimText};
}
.rexy-cal .react-calendar__tile {
  padding: 6px 2px 4px;
  position: relative;
}
.rexy-cal .react-calendar__tile:enabled:hover,
.rexy-cal .react-calendar__tile:enabled:focus {
  background: ${REXY_CONSOLE.badgeOkBg};
  box-shadow: inset 0 1px 0 rgba(0,191,255,0.15);
}
.rexy-cal .react-calendar__tile--now {
  background: ${REXY_CONSOLE.badgeWrnBg};
}
.rexy-cal .react-calendar__tile--active {
  background: ${REXY_CONSOLE.badgeOkBg} !important;
  box-shadow: inset 0 0 0 1px ${REXY_CONSOLE.cardBorder},
              0 2px 8px rgba(0,0,0,0.5);
  color: ${REXY_CONSOLE.primary};
}
`;

function Dots({ counts }: { counts: DayCounts | undefined }) {
  if (!counts) return null;
  return (
    <span style={styles.dotRow}>
      {counts.events > 0 && <span style={{ ...styles.dot, background: REXY_CONSOLE.primary }} />}
      {counts.reports > 0 && <span style={{ ...styles.dot, background: REXY_CONSOLE.gold }} />}
      {counts.notes > 0 && <span style={{ ...styles.dot, background: REXY_CONSOLE.secondary }} />}
    </span>
  );
}

function StoreStatus({ label, status }: { label: string; status: string }) {
  const bad = status !== 'ok' && status !== 'fresh' && status !== 'absent';
  return (
    <span style={{ ...styles.statusChip, color: bad ? REXY_CONSOLE.critical : REXY_CONSOLE.dimText }}>
      {label}:{status}
    </span>
  );
}

export default function RexyCalendarPanel() {
  const {
    monthDays, monthStatus, monthErr,
    viewDate, setViewDate,
    selected, day, dayErr,
    tab, setTab,
    openDay,
  } = useCalendar();

  return (
    <div style={styles.card} className="rexy-cal">
      <style>{CAL_CSS}</style>
      <div style={styles.sectionHeader}>║ REXY CALENDAR</div>
      <div style={styles.subtle}>
        a window on what the harness already holds · activity ● reports ● notes ●
      </div>
      {monthErr && <div style={styles.loud}>month view not_available — {monthErr}</div>}
      {/* GHOST HOLD item 1 — FAIL-LOUD at the audit surface: a store the month
          view could not read is a visible warning; days below may be showing
          empty that are NOT. Never silent emptiness. */}
      {Object.entries(monthStatus)
        .filter(([, s]) => s !== 'ok' && s !== 'fresh')
        .map(([store, s]) => (
          <div key={store} style={styles.loud}>
            ⚠ {store} store {s} — this month may show empty days that are NOT empty
          </div>
        ))}
      <Calendar
        onClickDay={openDay}
        onActiveStartDateChange={({ activeStartDate }) => {
          if (activeStartDate) setViewDate(activeStartDate);
        }}
        tileContent={({ date, view }) =>
          view === 'month' ? <Dots counts={monthDays[dayKey(date)]} /> : null}
        showNeighboringMonth={false}
      />

      {selected && (
        <div style={styles.dayPanel}>
          <div style={styles.dayHeader}>
            <span style={{ color: REXY_CONSOLE.primary }}>{selected}</span>
            {day && (
              <span style={styles.snapshotTag}>
                {day.snapshot.status === 'ok'
                  ? `snapshot ${day.snapshot.cell?.cell_id || ''}`
                  : day.snapshot.status === 'absent'
                    ? 'no snapshot — explicit absence'
                    : 'snapshot UNREADABLE — preserved for inspection'}
              </span>
            )}
          </div>
          {dayErr && <div style={styles.loud}>day view failed — {dayErr}</div>}
          {!day && !dayErr && <div style={styles.degraded}>loading…</div>}
          {day && (
            <>
              <div style={styles.statusRow}>
                <StoreStatus label="activity" status={day.activity.status} />
                <StoreStatus label="reports" status={day.reports.status} />
                <StoreStatus label="notes" status={day.notes.status} />
                <StoreStatus label="pulse" status={day.pulse.status} />
              </div>

              {/* THE AUDIT SURFACE: the day's timestamped activity, whose it was
                  (agent-level WHO, rendered as given), beside the claims. */}
              <div style={styles.blockHeader}>ACTIVITY · {day.activity.events.length}</div>
              {day.activity.status !== 'ok' ? (
                <div style={styles.loud}>activity store {day.activity.status} — nothing shown is invented</div>
              ) : day.activity.events.length === 0 ? (
                <div style={styles.degraded}>no recorded activity this day</div>
              ) : (
                <div style={styles.list}>
                  {day.activity.events.map((e: any, i: number) => (
                    <div key={`a${i}`} style={styles.itemRow}>
                      <span style={styles.itemAgent}>{e.pulse_payload?.who || '—'}</span>
                      <span style={styles.itemSub}>
                        {e.pulse_payload?.when?.slice(11, 16) || '—'} · {e.pulse_payload?.where || '—'} · {e.pulse_payload?.subject || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* NOTES / REPORTS — the §4.12 split: NOTES = her dreamstate
                  (user-facing); REPORTS = the structured role artifacts. */}
              <div style={styles.tabRow}>
                {(['reports', 'notes'] as const).map((t) => (
                  <button key={t} onClick={() => setTab(t)}
                    style={{ ...styles.tabBtn, ...(tab === t ? styles.tabActive : {}) }}>
                    {t.toUpperCase()}
                  </button>
                ))}
              </div>
              {tab === 'reports' && (
                day.reports.status !== 'ok' && day.reports.status !== 'fresh' ? (
                  <div style={styles.loud}>report store {day.reports.status}</div>
                ) : day.reports.items.length === 0 ? (
                  <div style={styles.degraded}>no reports composed this day</div>
                ) : (
                  <div style={styles.list}>
                    {day.reports.items.map((r: any) => (
                      <div key={r.report_id} style={styles.reportBlock}>
                        <div style={styles.itemAgent}>
                          {r.report_id} · {r.kind} · {r.status}
                        </div>
                        {/* Piece 2: new artifacts carry `content` — her authored body,
                            verbatim; pre-rebuild artifacts fall back to `statement`. */}
                        <div style={styles.reportStatement}>{r.content || r.statement}</div>
                        <div style={styles.itemSub}>
                          {/* Piece 2: rebuilt artifacts count notes_total; legacy count events_total. */}
                          membership: {(r.membership || []).join(', ') || 'none'} ·{' '}
                          {r.notes_total != null ? `notes ${r.notes_total}` : `events ${r.events_total ?? 0}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
              {tab === 'notes' && (
                day.notes.status !== 'ok' ? (
                  <div style={styles.loud}>notes {day.notes.status}</div>
                ) : day.notes.items.length === 0 ? (
                  <div style={styles.degraded}>
                    no dreamstate notes this day (Stage 4 writes them; this panel only displays)
                  </div>
                ) : (
                  <div style={styles.list}>
                    {day.notes.items.map((n: any) => (
                      <div key={n.id} style={styles.reportBlock}>
                        <div style={styles.itemSub}>{n.created_at?.slice(11, 16)}</div>
                        <div style={styles.noteContent}>{n.content}</div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  sectionHeader: {
    fontWeight: 700,
    fontFamily: MONO, fontSize: 12, letterSpacing: 2,
    color: REXY_CONSOLE.primary, marginBottom: 10,
  },
  subtle: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.dimText, marginBottom: 8 },
  dotRow: { display: 'flex', gap: 2, justifyContent: 'center', marginTop: 2, minHeight: 5 },
  dot: { width: 4, height: 4, borderRadius: 2, display: 'inline-block' },
  dayPanel: {
    marginTop: 10, paddingTop: 10,
    borderTop: `1px solid ${REXY_CONSOLE.divider}`,
  },
  dayHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    fontFamily: MONO, fontSize: 11, letterSpacing: 1, marginBottom: 6,
  },
  snapshotTag: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText },
  statusRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  statusChip: { fontFamily: MONO, fontSize: 8, letterSpacing: 1 },
  blockHeader: {
    fontFamily: MONO, fontSize: 9, letterSpacing: 1.5,
    color: REXY_CONSOLE.dimText, margin: '6px 0 4px',
  },
  list: { display: 'flex', flexDirection: 'column', gap: 2 },
  itemRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: 10, padding: '3px 4px', borderBottom: `1px solid ${REXY_CONSOLE.rowDivider}`,
  },
  itemAgent: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.primary },
  itemSub: {
    fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
  },
  tabRow: { display: 'flex', gap: 6, margin: '10px 0 6px' },
  tabBtn: {
    fontFamily: MONO, fontSize: 9, letterSpacing: 1.5, cursor: 'pointer',
    color: REXY_CONSOLE.dimText, background: 'transparent',
    border: `1px solid ${REXY_CONSOLE.divider}`, borderRadius: 4, padding: '3px 10px',
  },
  tabActive: {
    color: REXY_CONSOLE.primary, background: REXY_CONSOLE.badgeOkBg,
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    boxShadow: 'inset 0 1px 0 rgba(0,191,255,0.15)',
  },
  reportBlock: {
    padding: '6px 8px', borderRadius: 4,
    border: `1px solid ${REXY_CONSOLE.rowDivider}`, background: REXY_CONSOLE.panel,
    boxShadow: '0 2px 6px rgba(0,0,0,0.35)', marginBottom: 4,
  },
  reportStatement: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.infText, margin: '3px 0' },
  noteContent: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.infText, lineHeight: 1.5 },
  degraded: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.dimText, padding: '4px 0' },
  loud: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.critical, padding: '4px 0' },
};
